import { isNil } from 'lodash';

import { createApi, fetcher } from '../../api';

import { GetBlastsRequest, GetBlastsResponse, SERVERSIDE_BEEHIIV_ENDPOINTS } from './beehiiv.model';
import { BeehiivPublicationSlugs, getPublicationIdBySlug } from './constants';
import {
    GetPostRequest,
    GetPostResponse,
    GetPostsRequest,
    GetPostsResponse,
    GetPublicationsResponse,
    GetSubscriptionByEmailRequest,
    GetSubscriptionByEmailResponse,
    PostUpdateSubscriptionRequest,
    SubscribeToPublicationInputs,
} from './models';

const beehiivServersideApi = createApi(build => ({
    get: {
        blasts: build<GetBlastsResponse, GetBlastsRequest>({
            cachePrefix: 'serverside-beehiiv-blasts',
            baseUrl: 'beehiiv',
            query: ({ limit = 24, publication, expand, direction, order_by = 'last_sent', page }) => {
                return {
                    uri: SERVERSIDE_BEEHIIV_ENDPOINTS.GET.BLASTS({
                        publicationId: getPublicationIdBySlug(publication as BeehiivPublicationSlugs),
                    }),
                    params: {
                        limit,
                        direction,
                        order_by,
                        page,
                        expand,
                    },
                    options: {
                        headers: { Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}` },
                    },
                };
            },
        }),
        post: build<GetPostResponse, GetPostRequest>({
            cachePrefix: 'serverside-beehiiv-post',
            baseUrl: 'beehiiv',
            query: ({ publication, postId, expand = ['premium_web_content'] }) => {
                return {
                    uri: SERVERSIDE_BEEHIIV_ENDPOINTS.GET.POST({
                        publicationId: getPublicationIdBySlug(publication as BeehiivPublicationSlugs),
                        postId,
                    }),
                    params: {
                        expand,
                    },
                    options: {
                        headers: { Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}` },
                    },
                };
            },
        }),
        posts: build<GetPostsResponse, GetPostsRequest>({
            cachePrefix: 'serverside-beehiiv-posts',
            baseUrl: 'beehiiv',
            query: ({
                audience,
                content_tags,
                direction,
                expand,
                publication,
                hidden_from_feed,
                limit = 24,
                order_by,
                platform,
                status = 'confirmed',
                page,
            }) => {
                return {
                    uri: SERVERSIDE_BEEHIIV_ENDPOINTS.GET.POSTS({
                        publicationId: getPublicationIdBySlug(publication as BeehiivPublicationSlugs),
                    }),
                    params: {
                        audience,
                        content_tags,
                        hidden_from_feed,
                        platform,
                        status,
                        limit,
                        direction,
                        order_by,
                        page,
                        expand,
                    },
                    options: {
                        headers: { Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}` },
                    },
                };
            },
        }),
        publications: build<GetPublicationsResponse, undefined>({
            cachePrefix: 'serverside-beehiiv-publications',
            baseUrl: 'beehiiv',
            query: () => {
                return {
                    uri: SERVERSIDE_BEEHIIV_ENDPOINTS.GET.PUBLICATIONS(),
                    options: {
                        headers: { Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}` },
                    },
                };
            },
        }),
        subscriptionByEmail: build<GetSubscriptionByEmailResponse, GetSubscriptionByEmailRequest>({
            cachePrefix: 'serverside-beehiiv-subscription-by-email',
            baseUrl: 'beehiiv',
            query: ({ email, publicationId, expand }) => {
                return {
                    uri: SERVERSIDE_BEEHIIV_ENDPOINTS.GET.SUBSCRIPTION_BY_EMAIL({
                        email,
                        publicationId,
                    }),
                    options: {
                        headers: { Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}` },
                    },
                    params: {
                        expand,
                    },
                };
            },
        }),
    },
    post: {
        subscribeToPublication: build<any, SubscribeToPublicationInputs>({
            cachePrefix: 'serverside-subscribe-beehiiv',
            baseUrl: 'beehiiv',
            query: ({
                publication,
                email,
                source,
                campaign,
                medium,
                sourcePath,
                reactivate_existing = false,
                send_welcome_email = true,
                referring_site = `blockworks.co${sourcePath}`,
                custom_fields = [],
                double_opt_override = undefined,
            }) => ({
                uri: SERVERSIDE_BEEHIIV_ENDPOINTS.POST.SUBSCRIBE_BEEHIIV({
                    publicationId: getPublicationIdBySlug(publication as BeehiivPublicationSlugs),
                }),
                options: {
                    method: fetcher.Method.Post,
                    headers: {
                        Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}`,
                    },
                    body: {
                        email,
                        reactivate_existing,
                        send_welcome_email,
                        utm_source: source,
                        utm_campaign: campaign,
                        utm_medium: medium,
                        referring_site,
                        custom_fields,
                        double_opt_override,
                    },
                },
            }),
        }),
    },
    put: {
        updateSubscription: build<any, PostUpdateSubscriptionRequest>({
            cachePrefix: 'serverside-put-subscription-beehiiv',
            baseUrl: 'beehiiv',
            query: ({ publicationId, subscriptionId, tier, stripeCustomerId, unsubscribe, customFields = [] }) => ({
                uri: SERVERSIDE_BEEHIIV_ENDPOINTS.PUT.UPDATE_SUBSCRIPTION({
                    publicationId,
                    subscriptionId,
                }),
                options: {
                    method: fetcher.Method.Put,
                    headers: {
                        Authorization: `Bearer ${process.env.BEEHIIV_API_KEY}`,
                    },
                    body: {
                        ...(!isNil(tier) && { tier }),
                        ...(!isNil(stripeCustomerId) && { stripe_customer_id: stripeCustomerId }),
                        ...(!isNil(unsubscribe) && { unsubscribe }),
                        ...(!isNil(customFields) && customFields.length > 0 && { custom_fields: customFields }),
                    },
                },
            }),
        }),
    },
}));

export { beehiivServersideApi };
